import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../config/config";
import { toast } from "react-toastify";

const dict = {
  en: {
    resetPassword: "Reset Password",
    newPassword: "New Password",
    confirmPassword: "Confirm New Password",
    submit: "Update Password",
    backToLogin: "Back to Login",
    loading: "Updating...",
    passwordLength: "Password must be at least 6 characters",
    passwordMismatch: "Passwords do not match",
    successMessage: "Password updated successfully!",
    errorMessage: "Invalid or expired reset link. Please try again.",
    passwordPlaceholder: "Enter new password",
    confirmPlaceholder: "Confirm new password",
  },
  ar: {
    resetPassword: "إعادة تعيين كلمة المرور",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور الجديدة",
    submit: "تحديث كلمة المرور",
    backToLogin: "العودة إلى تسجيل الدخول",
    loading: "جاري التحديث...",
    passwordLength: "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
    passwordMismatch: "كلمتا المرور غير متطابقتين",
    successMessage: "تم تحديث كلمة المرور بنجاح!",
    errorMessage: "رابط إعادة التعيين غير صالح أو منتهي الصلاحية. يرجى المحاولة مرة أخرى.",
    passwordPlaceholder: "أدخل كلمة المرور الجديدة",
    confirmPlaceholder: "تأكيد كلمة المرور الجديدة",
  }
};

export default function ResetPassword({ lang }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const isAr = lang?.Lang === "ar" || localStorage.getItem("AC-lang") === "ar";
  const t = isAr ? dict.ar : dict.en;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error(t.passwordLength);
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t.passwordMismatch);
      return;
    }

    // Parse token and email from URL parameters
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const email = params.get("email");

    if (!token || !email) {
      toast.error(t.errorMessage);
      return;
    }

    setLoading(true);
    axios
      .post(`${apiKey}/reset-password`, {
        email,
        token,
        newPassword: password,
      })
      .then((res) => {
        toast.success(t.successMessage);
        setTimeout(() => {
          navigate("/");
        }, 1500);
      })
      .catch((err) => {
        console.error(err);
        toast.error(err.response?.data?.Error || t.errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      className="container d-flex justify-content-center align-items-center"
      style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}
      dir={isAr ? "rtl" : "ltr"}
    >
      <div
        className="card shadow-lg p-5 border-0"
        style={{
          width: "100%",
          maxWidth: "450px",
          borderRadius: "16px",
          backgroundColor: "#ffffff",
        }}
      >
        <div className="text-center mb-4">
          <div className="mb-3 d-inline-block p-3 rounded-circle" style={{ backgroundColor: "#eff6ff" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              fill="#3b82f6"
              viewBox="0 0 16 16"
            >
              <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1V3a3 3 0 0 1 6 0v4h1V3a2 2 0 0 0-2-2z" />
            </svg>
          </div>
          <h2 className="font-weight-bold text-dark" style={{ fontSize: "1.6rem" }}>
            {t.resetPassword}
          </h2>
          <p className="text-muted mt-2" style={{ fontSize: "0.9rem" }}>
            {isAr ? "يرجى إدخال كلمة المرور الجديدة وتأكيدها." : "Please enter and confirm your new password below."}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label className="text-secondary font-weight-bold mb-2" style={{ fontSize: "0.85rem" }}>
              {t.newPassword}
            </label>
            <input
              type="password"
              className="form-control form-control-lg"
              placeholder={t.passwordPlaceholder}
              style={{
                borderRadius: "10px",
                border: "1.5px solid #cbd5e1",
                fontSize: "1rem",
                padding: "12px 16px",
              }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group mb-4">
            <label className="text-secondary font-weight-bold mb-2" style={{ fontSize: "0.85rem" }}>
              {t.confirmPassword}
            </label>
            <input
              type="password"
              className="form-control form-control-lg"
              placeholder={t.confirmPlaceholder}
              style={{
                borderRadius: "10px",
                border: "1.5px solid #cbd5e1",
                fontSize: "1rem",
                padding: "12px 16px",
              }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg w-100 font-weight-bold py-3 mb-3"
            style={{
              borderRadius: "10px",
              fontSize: "1rem",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              border: "none",
            }}
            disabled={loading}
          >
            {loading ? t.loading : t.submit}
          </button>

          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-link text-decoration-none font-weight-bold"
              style={{ fontSize: "0.9rem", color: "#3b82f6" }}
              onClick={() => navigate("/")}
              disabled={loading}
            >
              {t.backToLogin}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
